import React from "react";

import { Card, EmptyState, Segmented, Stat, StatGrid, Table } from "../../../Ui/index.jsx";
import { AdminTab, TabLoading, dayWindows } from "./adminShared.jsx";

// AI usage: estimated LLM cost and tokens per day / model / purpose.
const usd = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: n < 1 ? 4 : 2, maximumFractionDigits: 4 })}`;
const compactNum = (n) => Number(n || 0).toLocaleString();

function CostBars({ data }) {
  const max = Math.max(...data.map((d) => d.cost), 0.0000001);
  return (
    <div className="Adm-Bars">
      {data.map((d) => (
        <div key={d.date} className="Adm-Bars__col" title={`${d.date} · ${usd(d.cost)} · ${d.calls} calls`}>
          <div className="Adm-Bars__bar" style={{ "--bar-h": `${Math.max(2, (d.cost / max) * 100)}%` }} />
          <span className="ds-small ds-num">{d.label}</span>
        </div>
      ))}
    </div>
  );
}

export function AiUsageTab({ usage, days, setDays }) {
  if (!usage) return <TabLoading>Loading AI usage…</TabLoading>;
  const t = usage.totals || {};
  const none = <EmptyState>No AI calls in this window.</EmptyState>;
  const byModel = usage.by_model || [];
  const byPurpose = usage.by_purpose || [];
  return (
    <AdminTab title="AI Models · Usage" subtitle="Where LLM tokens go, per day / model / purpose. Cost is estimated from public rates."
      actions={<Segmented label="Window" options={dayWindows([7, 14, 30])} value={days} onChange={setDays} />}>
      <StatGrid>
        <Stat label={`Est. cost · ${usage.days}d`} value={usd(t.cost)} />
        <Stat label="LLM calls" value={compactNum(t.calls)} />
        <Stat label="Tokens in" value={compactNum(t.input_tokens)} hint={`${compactNum(t.output_tokens)} out`} />
        <Stat label="Avg / day" value={usd(t.avg_per_day)} hint="estimated" />
      </StatGrid>

      <Card title="Estimated cost per day (USD)"><CostBars data={usage.per_day || []} /></Card>

      <div className="Adm-Split">
        <Card title="By model">
          {byModel.length ? (
            <Table flush columns={[
              { key: "model", header: "Model", render: (m) => <span className="ds-code">{m.model}</span> },
              { key: "calls", header: "Calls", align: "right", render: (m) => compactNum(m.calls) },
              { key: "tokens", header: "Tokens", align: "right", render: (m) => compactNum(m.tokens) },
              { key: "cost", header: "Cost", align: "right", render: (m) => <span className="ds-strong">{usd(m.cost)}</span> },
            ]} rows={byModel.map((m) => ({ ...m, id: m.model }))} />
          ) : none}
        </Card>
        <Card title="By purpose">
          {byPurpose.length ? (
            <Table flush columns={[
              { key: "purpose", header: "Purpose" },
              { key: "calls", header: "Calls", align: "right", render: (p) => compactNum(p.calls) },
              { key: "cost", header: "Cost", align: "right", render: (p) => <span className="ds-strong">{usd(p.cost)}</span> },
            ]} rows={byPurpose.map((p) => ({ ...p, id: p.purpose }))} />
          ) : none}
        </Card>
      </div>
      <p className="ds-small">Cost is an estimate from public per-token rates · not a bill.</p>
    </AdminTab>
  );
}
